import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Linkedin } from "lucide-react";
import { useLang } from "../context/LanguageContext";

// Custom TikTok icon (lucide-react doesn't include brand TikTok)
function TikTokIcon({ size = 16, className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className={className}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M19.6 6.4a5.3 5.3 0 0 1-3.2-3.2h-3v13.3a2.6 2.6 0 1 1-2.6-2.6c.3 0 .5 0 .7.1v-3a5.6 5.6 0 1 0 4.9 5.5V9.7a8.2 8.2 0 0 0 4.8 1.5V8.1a5.2 5.2 0 0 1-1.6-.3c-.4-.1-.7-.3-1-.5Z" />
    </svg>
  );
}

export default function Footer() {
  const { t } = useLang();
  const year = new Date().getFullYear();

  const socials = [
    { href: t.footer.social.instagram, handle: t.footer.social.instagramHandle, Icon: Instagram, label: "Instagram", testId: "social-instagram" },
    { href: t.footer.social.tiktok, handle: t.footer.social.tiktokHandle, Icon: TikTokIcon, label: "TikTok", testId: "social-tiktok" },
    { href: t.footer.social.linkedin, handle: t.footer.social.linkedinHandle, Icon: Linkedin, label: "LinkedIn", testId: "social-linkedin" },
  ];

  return (
    <footer className="bg-[#0B1B29] text-white mt-auto" data-testid="site-footer">
      <div className="pgb-container py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="inline-block mb-6">
              <img
                src="/brand/planet-group-baku-logo.png"
                alt="Planet Group Baku"
                className="h-28 w-auto object-contain drop-shadow-[0_4px_20px_rgba(147,197,253,0.2)]"
              />
            </div>
            <p className="text-white/80 text-sm leading-relaxed max-w-md mb-6">
              {t.footer.tagline.en}<br />
              {t.footer.tagline.az}<br />
              {t.footer.tagline.ru}
            </p>
            <div className="flex items-start gap-3 text-sm text-white/80 mb-6">
              <MapPin size={16} className="mt-0.5 text-[#F59E0B]" />
              <span>{t.footer.address}</span>
            </div>

            {/* Brand partner — Guibang */}
            <div className="mb-6 inline-flex items-center gap-3 bg-white px-4 py-3" data-testid="footer-brand-partner">
              <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-[#3F4E5C]">
                {t.common.guibang.partner}
              </span>
              <div className="w-px h-6 bg-[#D8DEE4]" />
              <img src="/brand/guibang-logo.png" alt="Guibang" className="h-6 w-auto" />
            </div>

            {/* Social */}
            <div>
              <h4 className="text-xs font-semibold tracking-[0.22em] uppercase text-white/50 mb-3">
                {t.footer.social.title}
              </h4>
              <div className="flex flex-col gap-2">
                {socials.map(({ href, handle, Icon, label, testId }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={testId}
                    className="inline-flex items-center gap-3 text-sm text-white/85 hover:text-[#F59E0B] transition-colors group w-fit"
                  >
                    <span className="w-9 h-9 border border-white/20 group-hover:border-[#F59E0B] flex items-center justify-center transition-colors">
                      <Icon size={16} />
                    </span>
                    <span className="font-mono tracking-wide">{handle}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Divisions */}
          <div className="md:col-span-3">
            <h4 className="text-xs font-semibold tracking-[0.22em] uppercase text-white/50 mb-5">
              {t.footer.divisions}
            </h4>
            <ul className="space-y-3 text-sm">
              <li><Link className="link-underline text-white hover:text-[#93C5FD]" to="/ikel-mt" data-testid="footer-link-ikel">İKEL MT</Link></li>
              <li><Link className="link-underline text-white hover:text-[#93C5FD]" to="/furniture" data-testid="footer-link-furniture">Planet Group</Link></li>
              <li><Link className="link-underline text-white hover:text-[#93C5FD]" to="/logistics" data-testid="footer-link-logistics">Planet Group Baku</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <h4 className="text-xs font-semibold tracking-[0.22em] uppercase text-white/50 mb-5">
              {t.footer.contact}
            </h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-2 text-white/85">
                <Mail size={14} className="text-[#93C5FD] mt-1 shrink-0" />
                <div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-white/50">{t.footer.emailLabel}</div>
                  <a href={`mailto:${t.footer.email}`} className="link-underline break-all" data-testid="footer-email">
                    {t.footer.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-2 text-white/85">
                <Mail size={14} className="text-[#F59E0B] mt-1 shrink-0" />
                <div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-white/50">{t.footer.emailIkelLabel}</div>
                  <a href={`mailto:${t.footer.emailIkel}`} className="link-underline break-all" data-testid="footer-email-ikel">
                    {t.footer.emailIkel}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-2 text-white/85">
                <Phone size={14} className="text-[#93C5FD] mt-1 shrink-0" />
                <div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-white/50">{t.footer.phoneLabel}</div>
                  <a
                    href={`tel:${t.footer.phone.replace(/\s/g, "")}`}
                    className="link-underline"
                    data-testid="footer-phone"
                  >
                    {t.footer.phone}
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
          <span className="text-xs text-white/50 font-mono tracking-wider">
            © {year} Planet Group Baku. {t.footer.rights}
          </span>
          <span className="text-xs text-white/50 font-mono tracking-wider">
            Foshan  ·  Guangzhou  ·  Baku
          </span>
        </div>
      </div>
    </footer>
  );
}
